import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { AgenturModel } from 'app/models/agentur.model';
import { AgenturStore } from '../agentur.store';
import { BaseAgenturListUiStore } from './base.agentur.list.ui.store';
import { AgenturListUiStore } from './agt.list.ui.store';
import { AkumaCampaignModel, AkumaCampaignStore } from '../akuma.campaign.store';
import { AgtAkumaAktionenModel, AgtAkumaAktionenOverviewModel, AgtAkumaAktionenStore, IAgtAkumaAktionenModel, IAgtAkumaCampaignDetailModel } from '../agt.akumaaktionen.store';
import { isKwInDateRange } from 'app/utils';

export interface ICampaignDetailData {
	agt?: AgenturModel;
	anzahlAktionstreffer: number;
	zuweisungAgentur: number;
	zuweisungMitarbeiter: number;
	bearbeitungsQuote: number;
	bearbeitungsQuotePass: number;
}

const campaigtNotFound = new AkumaCampaignModel({
	key: '0',
	valueString: JSON.stringify({
		campaign_name: 'Nicht gefunden',
		focus_campaign: 0,
		top_campaign: 0,
		lob_uebergreifend: 0,
		lob_sach: 0,
		lob_leben: 0,
		lob_kranken: 0,
		lob_agi: 0,
		lob_wuestenrot: 0,
		lob_sonstiges: 0,
		ba_firmen: 0,
		ba_privat: 0,
		ba_sonstige: 0,
		campaign_start_date: new Date(0),
		campaign_end_date: new Date(0),
	}),
});

export class AkumaAktionenVM {
	constructor(agt: AgenturModel, campaigns: AkumaCampaignModel[]) {
		makeObservable(this);
		this.agt = agt;
		this.item = new Object({ vtnr_id: agt.agtId, kw: 0, campaign_number: 0 }) as IAgtAkumaAktionenModel;
		this.campaigns = campaigns;
	}

	agt: AgenturModel;

	@observable
	item: IAgtAkumaAktionenModel;

	@action
	setItem(item?: AgtAkumaAktionenModel) {
		if (item) {
			this.item = item;
			this.hasItem = true;
		} else {
			this.item = new AgtAkumaAktionenModel({ vtnr_id: this.agt.agtId, kw: 0, campaign_number: 0 });
			this.hasItem = false;
		}
	}

	@action
	setOverviewItem(item?: AgtAkumaAktionenOverviewModel) {
		if (item) {
			this.overviewItem = item;
			this.hasOverviewItem = true;
		} else {
			this.overviewItem = new AgtAkumaAktionenOverviewModel({ vtnr_id: this.agt.agtId, kw: 0, campaign_number: 0 });
			this.hasOverviewItem = false;
		}
	}

	@observable
	hasItem: boolean = false;

	@observable
	overviewItem?: AgtAkumaAktionenOverviewModel;

	@observable
	hasOverviewItem: boolean = false;

	campaigns: AkumaCampaignModel[];

	@computed
	get campaign() {
		const found = this.campaigns.find((c) => c.id === this.item.campaignNumber);
		if (found) {
			return found;
		}
		return campaigtNotFound;
	}
}

export class AgtAkumaAktionenUiStore extends BaseAgenturListUiStore<AkumaAktionenVM> {
	name = 'AgtAkumaAktionenUiStore';
	agenturStore: AgenturStore;
	agtAkumaAktionenStore: AgtAkumaAktionenStore;
	akumaCampaignStore: AkumaCampaignStore;

	constructor(agenturListUiStore: AgenturListUiStore, agenturStore: AgenturStore, agtAkumaAktionenStore: AgtAkumaAktionenStore, akumaCampaignStore: AkumaCampaignStore) {
		super(agenturListUiStore);
		makeObservable(this);
		this.agenturStore = agenturStore;
		this.agtAkumaAktionenStore = agtAkumaAktionenStore;
		this.akumaCampaignStore = akumaCampaignStore;
	}

	onLogout() {
		this.agenturStore.onLogout();
	}

	@observable
	selectedKw?: number;
	@observable
	campaigns: AkumaCampaignModel[] = [];

	@observable
	availableKws: number[] = [];

	@computed
	get maxKw() {
		if (this.availableKws.length === 0) {
			return 0;
		}
		return Math.max(...this.availableKws, 0);
	}

	@action
	setKw(kw?: number) {
		this.selectedKw = kw;
	}

	@action
	async _baseLoad() {
		this.loadingPromise = new Promise((resolve) => {
			this.loadingPromiseResolver = resolve;
		});

		const promises = await Promise.all([this.agenturStore.findAll(), this.akumaCampaignStore.getAllCampaigns(), this.agtAkumaAktionenStore.getDistinctKWs()]);

		let agts = promises[0].slice();
		this.campaigns = promises[1].slice();

		// Process data and create view models
		const res: AkumaAktionenVM[] = [];
		agts.forEach((agt) => {
			res.push(new AkumaAktionenVM(agt, this.campaigns));
		});

		const kws = promises[2];

		runInAction(() => {
			this.availableKws = kws.sort((a, b) => b - a);
			this.items = res;
		});

		// Set initial BM to latest if available
		if (this.availableKws.length > 0 && !this.selectedKw) {
			runInAction(() => {
				this.selectedKw = this.availableKws[0];
			});
		}

		this.loadingPromiseResolver?.(undefined);
	}

	async loadAgtAktionsDataByKw(kw?: number) {
		await this.load();
		if (!kw) {
			return;
		}
		const data = await this.agtAkumaAktionenStore.aggregateByAgt(kw, this.serverSideVmFilters);
		runInAction(() => {
			this.items.forEach((item) => {
				const found = data.find((x: any) => x.vtnrId === item.agt.agtId);
				item.setItem(found);
			});
		});
	}

	async loadCampaignCountOverviewByBm(kw?: number, agtId?: number) {
		await this.load();
		if (!kw) {
			return;
		}
		// Add agtId filter if specified
		let filters = [...this.serverSideVmFilters];
		if (agtId) {
			filters.push(`a.vtnr_id = ${agtId}`);
		}
		const data = await this.agtAkumaAktionenStore.aggregateCampaignCountsByAgt(kw, filters);
		runInAction(() => {
			this.items.forEach((item) => {
				const found = data.find((x: any) => x.vtnrId === item.agt.agtId);
				item.setOverviewItem(found);
			});
		});
	}

	async loadCampaignAktionsDataByKw(kw?: number) {
		await this.load();
		if (!kw) {
			return [];
		}
		// Combine both agency filters and VM-specific filters (campaign filters)
		const allFilters = [...this.serverSideAgtFilters, ...this.serverSideVmFilters];
		const data = await this.agtAkumaAktionenStore.aggregateByCampaign(kw, allFilters);
		const ret: AkumaCampaignModel[] = [];
		runInAction(() => {
			this.campaigns.forEach((campaign) => {
				// Check if the KW falls within the campaign's date range
				const isKwInRange = isKwInDateRange(kw, campaign.campaignStartDate, campaign.campaignEndDate);

				const found = data.find((x: any) => x.campaignNumber === campaign.id);
				campaign.setItem(found);

				// Only include campaigns that have data AND fall within the KW date range
				if (found && isKwInRange) {
					ret.push(campaign);
				}
			});
		});
		return ret;
	}

	async loadCampaignDetails(campaignNumber: number): Promise<ICampaignDetailData[]> {
		await this.load(); // Ensure agencies are loaded
		const campaignData = await this.agtAkumaAktionenStore.getCampaignDetailsByAgency(campaignNumber, this.selectedKw);

		// Merge with agency model and map lowercase keys to camelCase
		return campaignData.map((item: IAgtAkumaCampaignDetailModel) => {
			const agency = this.items.find((vm) => vm.agt.agtId === item.agtid);
			return {
				agt: agency?.agt,
				anzahlAktionstreffer: item.anzahlaktionstreffer,
				zuweisungAgentur: item.zuweisungagentur,
				zuweisungMitarbeiter: item.zuweisungmitarbeiter,
				zuweisungGesamt: item.zuweisungagentur + item.zuweisungmitarbeiter,
				bearbeitungsQuote: item.bearbeitungsquote,
				bearbeitungsQuotePass: item.bearbeitungsquotepass,
			};
		});
	}
}
